import { CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Bundle builder.
 *
 * Each column picks a category, then a product within it, then an image of that
 * product. Every column can choose any category, so two towels and a swaddle is
 * as valid as one of each.
 *
 * The whole catalogue is emitted once as JSON and the columns are built from it,
 * rather than rendering every category into every column -- that would be nine
 * product lists and hundreds of images for three visible cards.
 *
 * Nothing is a bundle product: the chosen products go into the cart as ordinary
 * line items, so stock decrements against the real products and the store's
 * automatic quantity discount applies by itself. Every line added together
 * carries the same gift-set code as a line item property, which is what tells
 * fulfilment these units are one set and need packing together.
 *
 * Telling the theme the cart changed is done the way the theme's own
 * add-to-cart does it -- dispatch a CartLinesUpdateEvent carrying a promise,
 * then resolve it with the section HTML the cart returns. Dispatching on
 * `document` matters: the cart-items component only re-renders for events whose
 * target it does not contain.
 */
class DdBundleBuilder extends HTMLElement {
  #state = [];
  #busy = false;

  connectedCallback() {
    const data = this.querySelector('[data-ddbb-catalog]');
    if (!data) return;

    try {
      this.catalog = JSON.parse(data.textContent);
    } catch {
      return;
    }

    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.currency = this.dataset.currency || 'USD';
    this.discount = parseFloat(this.dataset.discount || '0') || 0;
    this.propertyName = this.dataset.propertyName || 'Gift set';

    this.columns = [...this.querySelectorAll('[data-ddbb-col]')];
    this.#state = this.columns.map((col, i) => ({
      category: Math.min(Number(col.dataset.defaultCategory ?? i) || 0, this.catalog.length - 1),
      product: 0,
      image: 0,
    }));

    this.columns.forEach((_, i) => this.#buildColumn(i));

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.#renderTotals();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  #products(col) {
    return this.catalog[this.#state[col].category]?.products ?? [];
  }

  #current(col) {
    return this.#products(col)[this.#state[col].product];
  }

  #onChange = (event) => {
    const select = event.target.closest('[data-ddbb-category]');
    if (!select) return;
    const col = Number(select.closest('[data-ddbb-col]').dataset.ddbbCol);
    this.#state[col] = { category: Number(select.value), product: 0, image: 0 };
    this.#buildColumn(col);
    this.#renderTotals();
  };

  #onClick = (event) => {
    const thumb = event.target.closest('[data-ddbb-thumb]');
    if (thumb && !thumb.disabled) {
      const col = Number(thumb.closest('[data-ddbb-col]').dataset.ddbbCol);
      this.#state[col].product = Number(thumb.dataset.index);
      this.#state[col].image = 0;
      this.#renderColumn(col);
      this.#renderTotals();
      return;
    }

    const step = event.target.closest('[data-ddbb-img-step]');
    if (step) {
      const col = Number(step.closest('[data-ddbb-col]').dataset.ddbbCol);
      const images = this.#current(col)?.images ?? [];
      if (images.length > 1) {
        const delta = Number(step.dataset.ddbbImgStep);
        this.#state[col].image = (this.#state[col].image + delta + images.length) % images.length;
        this.#renderColumn(col);
      }
      return;
    }

    const dot = event.target.closest('[data-ddbb-dot]');
    if (dot) {
      const col = Number(dot.closest('[data-ddbb-col]').dataset.ddbbCol);
      this.#state[col].image = Number(dot.dataset.index);
      this.#renderColumn(col);
      return;
    }

    if (event.target.closest('[data-ddbb-add]')) this.#addToCart();
  };

  /** Rebuild the thumbnail strip after the category changes. */
  #buildColumn(col) {
    const column = this.columns[col];
    const strip = column.querySelector('[data-ddbb-thumbs]');
    const products = this.#products(col);

    strip.replaceChildren();
    products.forEach((product, index) => {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ddbb__thumb';
      button.dataset.ddbbThumb = '';
      button.dataset.index = String(index);
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-label', `${product.title} ${product.priceDisplay}`);
      if (!product.available) button.disabled = true;

      const src = product.images[0]?.thumb;
      if (src) {
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        img.className = 'ddbb__thumb-img';
        button.append(img);
      }
      li.append(button);
      strip.append(li);
    });

    // Land on something buyable rather than a sold-out first entry.
    const firstAvailable = products.findIndex((p) => p.available);
    this.#state[col].product = firstAvailable === -1 ? 0 : firstAvailable;
    this.#state[col].image = 0;

    this.#renderColumn(col);
  }

  #renderColumn(col) {
    const column = this.columns[col];
    const product = this.#current(col);
    const images = product?.images ?? [];
    const image = images[this.#state[col].image] || images[0];

    const hero = column.querySelector('[data-ddbb-hero-img]');
    if (hero) {
      if (image) {
        hero.src = image.hero;
        hero.alt = product.title;
        hero.hidden = false;
      } else {
        hero.removeAttribute('src');
        hero.hidden = true;
      }
    }

    const name = column.querySelector('[data-ddbb-name]');
    if (name) name.textContent = product?.title ?? '';
    const price = column.querySelector('[data-ddbb-price]');
    if (price) price.textContent = product?.priceDisplay ?? '';

    column.querySelectorAll('[data-ddbb-thumb]').forEach((el) => {
      const chosen = Number(el.dataset.index) === this.#state[col].product;
      el.setAttribute('aria-checked', chosen ? 'true' : 'false');
      el.classList.toggle('is-selected', chosen);
    });

    // Image controls only earn their place when there is more than one image.
    const multiple = images.length > 1;
    column.querySelectorAll('[data-ddbb-img-step]').forEach((el) => {
      el.hidden = !multiple;
    });

    const dots = column.querySelector('[data-ddbb-dots]');
    if (dots) {
      dots.hidden = !multiple;
      dots.replaceChildren();
      if (multiple) {
        images.forEach((_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'ddbb__dot';
          dot.dataset.ddbbDot = '';
          dot.dataset.index = String(index);
          dot.setAttribute('aria-label', `Image ${index + 1} of ${images.length}`);
          dot.classList.toggle('is-active', index === this.#state[col].image);
          dots.append(dot);
        });
      }
    }
  }

  #money(cents) {
    try {
      return formatMoney(cents, this.moneyFormat, this.currency);
    } catch {
      return String(Math.round(cents / 100));
    }
  }

  #chosen() {
    return this.columns.map((_, col) => this.#current(col)).filter((p) => p && p.available);
  }

  /**
   * A short code shared by every line of one gift set. Unique per add, so two
   * sets in the same order stay tellable apart. Ambiguous characters (I, O, 0,
   * 1) are left out because someone reads this off a printed packing slip.
   */
  #newSetCode() {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 4; i += 1) {
      code += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    return code;
  }

  #renderTotals() {
    const chosen = this.#chosen();
    const complete = chosen.length === this.columns.length;
    const subtotal = chosen.reduce((sum, p) => sum + p.price, 0);
    // Round the way Shopify does so this matches the cart exactly.
    const savings = complete ? Math.round((subtotal * this.discount) / 100) : 0;

    const totalEl = this.querySelector('[data-ddbb-total]');
    if (totalEl) totalEl.textContent = complete ? this.#money(subtotal - savings) : '';

    const button = this.querySelector('[data-ddbb-add]');
    if (button) button.disabled = !complete || this.#busy;

    const save = this.querySelector('[data-ddbb-save]');
    if (save) {
      save.hidden = !complete || savings <= 0;
      if (complete && savings > 0) {
        save.replaceChildren();
        const was = document.createElement('s');
        was.textContent = this.#money(subtotal);
        save.append(was, ` ${(save.dataset.template || '').replace('[percent]', this.discount)}`);
      }
    }
  }

  async #addToCart() {
    if (this.#busy) return;
    const chosen = this.#chosen();
    if (chosen.length !== this.columns.length) return;

    this.#busy = true;
    const button = this.querySelector('[data-ddbb-add]');
    if (button) {
      button.disabled = true;
      button.classList.add('is-loading');
    }

    // The same product can be picked in more than one column, so send one line
    // with a quantity rather than the same variant twice. The gift-set property
    // also keeps these lines separate from the same product added on its own.
    const setCode = this.#newSetCode();
    const quantities = new Map();
    chosen.forEach((p) => quantities.set(p.vid, (quantities.get(p.vid) || 0) + 1));
    const items = [...quantities].map(([id, quantity]) => ({
      id: Number(id),
      quantity,
      properties: { [this.propertyName]: setCode },
    }));

    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component')]
          .map((el) => el.dataset.sectionId)
          .filter(Boolean)
      ),
    ];

    let settle;
    const promise = new Promise((resolve) => {
      settle = resolve;
    });

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise,
      })
    );

    try {
      const url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add to cart');
      }

      settle({ detail: { sections: data.sections } });
      document.querySelector('#cart-drawer')?.open?.();
    } catch (error) {
      settle({ detail: { didError: true } });
      const save = this.querySelector('[data-ddbb-save]');
      if (save) {
        save.hidden = false;
        save.textContent = error.message || 'Something went wrong. Please try again.';
      }
      console.warn('[dd-bundle-builder]', error);
    } finally {
      this.#busy = false;
      if (button) button.classList.remove('is-loading');
      this.#renderTotals();
    }
  }
}

if (!customElements.get('dd-bundle-builder')) {
  customElements.define('dd-bundle-builder', DdBundleBuilder);
}
